import React from 'react';

interface Modifiers {
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
}

interface Router {
  open: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

interface RouterProviderProps {
  navigate: (path: string) => void;
  children: React.ReactNode;
}

const RouterContext = React.createContext<Router | undefined>(undefined);

function shouldClientNavigate(link: HTMLAnchorElement, modifiers: Modifiers) {
  // Use getAttribute here instead of link.target. Firefox will default link.target to "_parent" when inside an iframe.
  const target = link.getAttribute('target');
  return (
    (!target || target === '_self') &&
    link.origin === window.location.origin &&
    !link.hasAttribute('download') &&
    !modifiers.metaKey && // open in new tab (mac)
    !modifiers.ctrlKey && // open in new tab (windows)
    !modifiers.altKey && // download
    !modifiers.shiftKey
  );
}

/**
 * A RouterProvider accepts a `navigate` function from a framework or client side router,
 * and provides it to all nested links to enable client side navigation.
 */
export function RouterProvider({ navigate, children }: RouterProviderProps) {
  const ctx = React.useMemo(
    () => ({
      open: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (shouldClientNavigate(event.currentTarget, event)) {
          event.preventDefault();
          navigate(event.currentTarget.href);
        }
      },
    }),
    [navigate],
  );

  return (
    <RouterContext.Provider value={ctx}>{children}</RouterContext.Provider>
  );
}
RouterProvider.displayName = 'RouterProvider';

export function useRouter(): Router | undefined {
  return React.useContext(RouterContext);
}

export function useRouterMutateOnClick(
  props: Partial<
    | React.HTMLAttributes<HTMLAnchorElement>
    | React.HTMLAttributes<HTMLButtonElement>
  >,
) {
  const router = useRouter();
  if (router?.open) {
    const { onClick } = props as React.AnchorHTMLAttributes<HTMLAnchorElement>;
    props.onClick = (
      event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => {
      if (onClick) onClick(event);
      router.open(event);
    };
  }
}
